import React, { useEffect, useMemo, useState } from 'react'
import { FaDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader } from '../components/ui'
import { SampleDataNotice } from '../components/Notices'
import RevenueChart from '../components/charts/RevenueChart'
import BarList from '../components/charts/BarList'
import { money, moneyShort, number, percent, formatDate, signedPercent } from '../lib/format'
import { STATUS } from '../lib/viz'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * The full report, with the product table that the dashboard only summarises.
 *
 * A sortable table rather than more charts. Past about seven rows a chart stops
 * helping and a table starts: the operator wants to rank ninety products by four
 * different measures and read exact figures, which is what tables are for.
 */

const COLUMNS = [
  { key: 'name', label: 'Product', align: 'left', format: (r) => r.name, sort: (a, b) => a.name.localeCompare(b.name) },
  { key: 'category', label: 'Category', align: 'left', format: (r) => r.category ?? '—', sort: (a, b) => (a.category ?? '').localeCompare(b.category ?? '') },
  { key: 'price', label: 'Price', align: 'right', format: (r) => money(r.price), numeric: true },
  { key: 'units_sold', label: 'Sold', align: 'right', format: (r) => number(r.units_sold), numeric: true },
  { key: 'revenue', label: 'Revenue', align: 'right', format: (r) => money(r.revenue), numeric: true },
  { key: 'views', label: 'Views', align: 'right', format: (r) => number(r.views), numeric: true },
  {
    key: 'conversion_rate',
    label: 'Conversion',
    align: 'right',
    numeric: true,
    format: (r) => (r.conversion_rate === null || r.conversion_rate === undefined ? '—' : percent(r.conversion_rate)),
  },
  // Dropped entirely rather than shown empty while stock is hidden: a column of
  // zeroes in an exported spreadsheet is a figure somebody will later quote.
  ...(SHOW_STOCK
    ? [{ key: 'stock_on_hand', label: 'Stock', align: 'right', format: (r) => number(r.stock_on_hand), numeric: true }]
    : []),
]

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
]

const AnalyticsPage = () => {
  const [range, setRange] = useState(90)
  const [sales, setSales] = useState([])
  const [performance, setPerformance] = useState([])
  const [categories, setCategories] = useState([])
  const [trending, setTrending] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [sortKey, setSortKey] = useState('revenue')
  const [sortDir, setSortDir] = useState('desc')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setState({ loading: true, error: null })
      try {
        const [daily, perf, cats, trend] = await Promise.all([
          api.getDailySales({ days: range }),
          api.getProductPerformance(),
          api.getCategoryPerformance(),
          api.getTrending(),
        ])
        if (cancelled) return
        setSales(daily ?? [])
        setPerformance(perf ?? [])
        setCategories(cats ?? [])
        setTrending(trend ?? [])
        setState({ loading: false, error: null })
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [range])

  const sorted = useMemo(() => {
    const column = COLUMNS.find((c) => c.key === sortKey)
    const factor = sortDir === 'asc' ? 1 : -1
    return [...performance].sort((a, b) => {
      if (column?.sort) return column.sort(a, b) * factor
      // Nulls always sort last regardless of direction — an unknown conversion
      // rate is not "the worst", it is absent.
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      return (Number(av) - Number(bv)) * factor
    })
  }, [performance, sortKey, sortDir])

  const headline = useMemo(() => {
    const revenue = sales.reduce((n, r) => n + (Number(r.revenue) || 0), 0)
    const orders = sales.reduce((n, r) => n + (Number(r.order_count) || 0), 0)
    const units = sales.reduce((n, r) => n + (Number(r.units) || 0), 0)
    const best = sales.reduce((top, r) => ((Number(r.revenue) || 0) > (Number(top?.revenue) || 0) ? r : top), null)
    return { revenue, orders, units, best }
  }, [sales])

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  /** CSV export. The spreadsheet is where a shop owner actually does arithmetic. */
  const exportCsv = () => {
    const header = [
      'Product', 'Code', 'Brand', 'Category', 'Price', 'Units sold', 'Revenue', 'Views', 'Conversion %',
      ...(SHOW_STOCK ? ['Stock'] : []),
    ]
    const rows = sorted.map((r) => [
      r.name, r.sku, r.brand ?? '', r.category ?? '', r.price,
      r.units_sold, r.revenue, r.views, r.conversion_rate ?? '',
      ...(SHOW_STOCK ? [r.stock_on_hand] : []),
    ])
    // Every field is quoted and inner quotes doubled — garment names contain
    // commas ("Navy, Red and Black") which would otherwise shift every column.
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\r\n')

    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `coquis-products-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (state.loading) return <Spinner label="Building the report…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load analytics.</strong> {state.error.message}</Banner>
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Analytics" subtitle={`Trading performance over the last ${range} days`}>
        <div className="flex rounded-lg border border-hairline bg-surface p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setRange(r.days)}
              className={`rounded-md px-2.5 py-1 text-2xs font-medium transition-colors ${
                range === r.days ? 'bg-brand-navy text-white' : 'text-ink-secondary hover:text-ink'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} className="btn-ghost">
          <FaDownload className="text-[11px]" />
          Export CSV
        </button>
      </PageHeader>

      <SampleDataNotice />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Figure label="Revenue" value={money(headline.revenue)} />
        <Figure label="Orders" value={number(headline.orders)} />
        <Figure label="Items sold" value={number(headline.units)} />
        <Figure
          label="Best day"
          value={headline.best ? moneyShort(headline.best.revenue) : '—'}
          hint={headline.best ? formatDate(headline.best.day) : null}
        />
      </div>

      <Card title="Revenue" subtitle={`Daily, paid orders only · last ${range} days`}>
        <RevenueChart data={sales} height={300} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Revenue by category">
          <BarList
            data={categories}
            valueKey="revenue"
            formatValue={moneyShort}
            secondary={(r) => `${number(r.units_sold)} sold · ${number(r.views)} views`}
            limit={12}
          />
        </Card>

        <Card title="Units sold by category">
          <BarList
            data={categories}
            valueKey="units_sold"
            formatValue={number}
            secondary={(r) =>
              `${number(r.product_count)} products${SHOW_STOCK ? ` · ${number(r.in_stock_count)} in stock` : ''}`
            }
            limit={12}
          />
        </Card>
      </div>

      {trending.length > 0 && (
        <Card title="Week-on-week movement" subtitle="Units sold in the last 7 days against the 7 before">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th className="num">This week</th>
                  <th className="num">Last week</th>
                  <th className="num">Change</th>
                </tr>
              </thead>
              <tbody>
                {[...trending]
                  .sort((a, b) => (b.units_7d ?? 0) - (a.units_7d ?? 0))
                  .slice(0, 15)
                  .map((row) => (
                    <tr key={row.id}>
                      <td className="max-w-[260px] truncate font-medium">{row.name}</td>
                      <td className="text-2xs text-ink-secondary">{row.category ?? '—'}</td>
                      <td className="num">{number(row.units_7d)}</td>
                      <td className="num text-ink-secondary">{number(row.units_prev_7d)}</td>
                      <td className="num font-semibold">
                        {row.units_change_pct === null ? (
                          <span className="text-2xs font-medium uppercase text-ink-muted">New</span>
                        ) : (
                          <span
                            style={{
                              color: row.units_change_pct >= 0 ? STATUS.goodText : STATUS.critical,
                            }}
                          >
                            {signedPercent(row.units_change_pct)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card
        title="Every product"
        subtitle={`${number(performance.length)} products · click a heading to sort`}
        bodyClassName=""
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {COLUMNS.map((column) => {
                  const active = sortKey === column.key
                  const Icon = !active ? FaSort : sortDir === 'asc' ? FaSortUp : FaSortDown
                  return (
                    <th
                      key={column.key}
                      className={column.align === 'right' ? 'text-right' : ''}
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        onClick={() => toggleSort(column.key)}
                        className={`inline-flex items-center gap-1.5 transition-colors hover:text-ink ${
                          active ? 'text-ink' : ''
                        }`}
                      >
                        {column.label}
                        <Icon className="text-[8px] opacity-60" aria-hidden="true" />
                      </button>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.id}>
                  {COLUMNS.map((column) => (
                    <td
                      key={column.key}
                      className={`${column.numeric ? 'num' : ''} ${
                        column.key === 'name' ? 'max-w-[260px] truncate font-medium' : ''
                      }`}
                    >
                      {column.format(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

const Figure = ({ label, value, hint }) => (
  <div className="card p-4">
    <div className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">{label}</div>
    <div className="mt-1.5 text-2xl font-semibold leading-none text-ink">{value}</div>
    {hint && <div className="mt-1.5 text-2xs text-ink-muted">{hint}</div>}
  </div>
)

export default AnalyticsPage
